import LScreen from "@components/LScreen";
import LScrollView from "@components/LScrollView";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LButton from "@components/LButton";
import LIcon from "@components/LIcon";
import { fonts } from "@theme";
import { Href, router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator } from "react-native";
import config from "@utils/config";
import { auth } from "@utils/firebase";
import { handoffTokenUser } from "@utils/handoff/handoffUrl";
import { signInAs } from "@utils/handoff/signInAs";
import { useData } from "../../runtime/provider";
import { useAuth } from "@utils/firebaseAuth/auth";
import { captureException } from "@utils/captureException";
import {
  clearStashedCustomToken,
  readStashedCustomToken,
  stashCustomToken,
} from "@utils/handoffCustomTokenStash";
import { AuthStatus } from "types/auth";

type ScreenState = "exchanging" | "success" | "error" | "already-signed-in";

const SUCCESS_REDIRECT_DELAY_MS = 1200;
// If the db-user fetch never settles (e.g. an account with no users row),
// don't strand the user on the success screen forever.
// How long the account may take to load after sign-in before the screen
// gives up. A first entry on a cold API can take several seconds; giving up
// early and going to "/" is not an option, because that skips the onboarding
// decision and the account then meets it on the next launch instead.
const STALLED_REDIRECT_DELAY_MS = 20000;

export default function TestLoginHandoffScreen() {
  // `next`: the content a Live Link open-in-app link was for; delivered
  // through the landing store once signed in, like any deferred deep link.
  const { h, next } = useLocalSearchParams<{ h: string; next?: string }>();
  const { status, firebaseUser } = useAuth();

  const [screenState, setScreenState] = useState<ScreenState>("exchanging");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const startedRef = useRef(false);

  // Capture the status the first time Firebase auth settles so we can decide,
  // during render, whether to greet an already-signed-in user or exchange the
  // token. Seeding this from render (rather than an effect) avoids a cascading
  // setState-in-effect and mirrors the derivation pattern in set-password.
  const [firstResolvedStatus, setFirstResolvedStatus] =
    useState<AuthStatus | null>(null);
  if (
    firstResolvedStatus === null &&
    status !== "firebase-auth-pending" &&
    status !== "db-user-pending"
  ) {
    setFirstResolvedStatus(status);
  }
  // Without a token a signed-in app has nothing to do. With one it exchanges
  // anyway: the fan proved that identity on the web seconds ago, so the
  // session is kept when the token names the same person and switched when
  // it does not (see `exchange`).
  const alreadySignedIn =
    !h &&
    (firstResolvedStatus === "logged-in" ||
      firstResolvedStatus === "firebase-user-logged-in");
  // Signed in and carrying a token that names this very user: the normal
  // case, a person tapping a link in an app they are already in. The content
  // opens at once with no screen and the token is never exchanged (it
  // expires unused). A token for somebody else, or one minted without the
  // hint, takes the exchange path and switches the session.
  // On a cold start the auth status can read signed in from the persisted
  // query cache before the Firebase instance has restored `auth.currentUser`,
  // so the user is taken from that same query first.
  const currentUid = firebaseUser?.uid ?? auth.currentUser?.uid ?? null;
  const bypass =
    !!h &&
    (firstResolvedStatus === "logged-in" ||
      firstResolvedStatus === "firebase-user-logged-in") &&
    handoffTokenUser(h) !== null &&
    handoffTokenUser(h) === currentUid;
  useEffect(() => {
    if (firstResolvedStatus === null) return;
    console.log("::Handoff:: decision", {
      firstResolvedStatus,
      hasToken: !!h,
      tokenUser: h ? handoffTokenUser(h) : null,
      currentUid,
      bypass,
      next,
    });
  }, [firstResolvedStatus, h, currentUid, bypass, next]);

  // A handoff link that arrives without its token is unrecoverable, so surface
  // it as an error derived from render rather than from a synchronous setState.
  const missingToken = firstResolvedStatus !== null && !alreadySignedIn && !h;

  const { reporting } = useData();
  const signIn = useCallback(
    (customToken: string) => signInAs(customToken, { auth, reporting }),
    [reporting],
  );

  // Pure token exchange: resolves with the Firebase custom token or throws.
  // Kept free of setState so the effect below can wire the result through
  // .then/.catch callbacks (matching the async pattern in set-password).
  //
  // The exchange CONSUMES the hand-off token server-side, so the window
  // between "consumed" and "Firebase session persisted" is the one place this
  // flow can strand a user for good: if the app dies in it (a startup OTA
  // reload is the real-world case), the token is spent, every retry 401s, and
  // the claim link is already used too. So the returned custom token is
  // stashed first and only cleared once sign-in succeeds — a later launch
  // finishes with the stash instead of re-exchanging. Custom tokens are
  // short-lived (~1h) and single-purpose, so a leftover stash is inert.
  const exchange = useCallback(async (): Promise<void> => {
    const stashed = await readStashedCustomToken();
    if (stashed) {
      await signIn(stashed);
      await clearStashedCustomToken();
      return;
    }

    const response = await fetch(
      `${config.API_URL}/api/rest/auth-handoff/exchange`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ handoffToken: h }),
      },
    );

    if (!response.ok) {
      throw new Error(`handoff exchange failed with ${response.status}`);
    }

    const { customToken } = (await response.json()) as {
      customToken: string;
    };
    await stashCustomToken(customToken);
    await signIn(customToken);
    await clearStashedCustomToken();
  }, [h, signIn]);

  const onExchangeError = useCallback((error: unknown) => {
    console.error("::TestLoginHandoffScreen - exchange error", error);
    captureException(error, {
      channel: "error",
      area: "auth",
      operation: "auth-handoff-exchange",
    });
    setScreenState("error");
    setErrorMessage(
      "We couldn't sign you in. This link may have expired — please try again.",
    );
  }, []);

  const onExchangeSuccess = useCallback(() => {
    setScreenState("success");
  }, []);

  // Navigate only once the auth pipeline has fully settled ("logged-in" = the
  // db user is loaded). Replacing mid-transition mounts the tab tree in a
  // half-initialized state (tabs and topbar render, every screen body blank).
  // The destination is the content itself, not the login flow's onboarding
  // decision: see the note inside.
  useEffect(() => {
    if (screenState !== "success") return;
    if (status === "db-user-error" || status === "no-user") {
      const failed = setTimeout(
        () =>
          onExchangeError(
            new Error(`account did not load after sign-in (${status})`),
          ),
        0,
      );
      return () => clearTimeout(failed);
    }
    if (status !== "logged-in") {
      // Still loading the account: keep waiting. Only a stall becomes an
      // error (with retry, which finishes from the stashed custom token).
      const stalled = setTimeout(
        () => onExchangeError(new Error("account load stalled after sign-in")),
        STALLED_REDIRECT_DELAY_MS,
      );
      return () => clearTimeout(stalled);
    }
    // The fan tapped a song on the Live Link; the song is what they get,
    // signed in, whatever the account's onboarding state. Home keeps its
    // setup and welcome surfaces for when they go there. The deferred
    // deep-link queue is not used: it holds content back until onboarding
    // is complete, which is the wrong order for a link someone just tapped.
    const content = next && next.startsWith("/") ? (next as Href) : "/";
    const timeout = setTimeout(
      () => router.replace(content),
      SUCCESS_REDIRECT_DELAY_MS,
    );
    return () => clearTimeout(timeout);
  }, [screenState, status, next, onExchangeError]);

  useEffect(() => {
    if (!bypass || startedRef.current) return;
    startedRef.current = true;
    const content = next && next.startsWith("/") ? (next as Href) : "/";
    router.replace(content);
  }, [bypass, next]);

  useEffect(() => {
    if (startedRef.current) return;
    if (firstResolvedStatus === null || alreadySignedIn || bypass || !h) return;

    startedRef.current = true;
    exchange().then(onExchangeSuccess).catch(onExchangeError);
  }, [
    firstResolvedStatus,
    alreadySignedIn,
    h,
    exchange,
    onExchangeSuccess,
    onExchangeError,
  ]);

  const goHome = useCallback(() => {
    router.replace("/");
  }, []);

  const handleRetry = useCallback(() => {
    setScreenState("exchanging");
    setErrorMessage(null);
    exchange().then(onExchangeSuccess).catch(onExchangeError);
  }, [exchange, onExchangeSuccess, onExchangeError]);

  const mode: ScreenState = alreadySignedIn
    ? "already-signed-in"
    : missingToken
      ? "error"
      : screenState;

  const displayError = missingToken
    ? "This sign-in link is missing its token."
    : errorMessage;

  const title =
    mode === "error"
      ? "Sign-in Failed"
      : mode === "already-signed-in"
        ? "Already Signed In"
        : mode === "success"
          ? "Signed In"
          : "Signing You In";

  if (bypass) return null;

  return (
    <LScreen>
      <LScrollView contentContainerStyle={{ flexGrow: 1 }}>
        <LBox flex={1} justifyContent="center" alignItems="center">
          <LBox width="100%" alignItems="center">
            <LBox alignItems="center">
              <LIcon name="logo_round_large" size={80} />
            </LBox>
            <LBox mb="m2" mt="l2" alignItems="center">
              <LText
                fontSize={28}
                lineHeight={33.6}
                fontFamily={fonts.semiBold}
              >
                {title}
              </LText>
              {mode === "exchanging" ? (
                <LBox mt="l2" alignItems="center">
                  <ActivityIndicator size="large" />
                </LBox>
              ) : mode === "error" && displayError ? (
                <LBox mt="s2" px="m1" alignItems="center">
                  <LText
                    fontSize={16}
                    lineHeight={20}
                    fontFamily={fonts.regular}
                    color="error"
                    textAlign="center"
                  >
                    {displayError}
                  </LText>
                </LBox>
              ) : mode === "success" ? (
                <LText
                  fontSize={16}
                  lineHeight={20}
                  fontFamily={fonts.regular}
                  color="greyText"
                  mt="s2"
                >
                  Taking you to Lissen…
                </LText>
              ) : mode === "already-signed-in" ? (
                <LText
                  fontSize={16}
                  lineHeight={20}
                  fontFamily={fonts.regular}
                  color="greyText"
                  mt="s2"
                  textAlign="center"
                >
                  You are already signed in on this device.
                </LText>
              ) : null}
            </LBox>
          </LBox>
        </LBox>
        <LBox width="100%" justifyContent="flex-end" gap="m1" pb="m1">
          {mode === "error" ? (
            <>
              {!missingToken ? (
                <LButton
                  label="TRY AGAIN"
                  variant="authActive"
                  onPress={handleRetry}
                  sentryEventName="test_login_handoff_retry"
                />
              ) : null}
              <LButton
                label="GO TO HOME"
                variant={missingToken ? "authActive" : "roundedWhite"}
                onPress={goHome}
                sentryEventName="test_login_handoff_home"
              />
            </>
          ) : mode === "already-signed-in" ? (
            <LButton
              label="GO TO HOME"
              variant="authActive"
              onPress={goHome}
              sentryEventName="test_login_handoff_home"
            />
          ) : null}
        </LBox>
      </LScrollView>
    </LScreen>
  );
}
